import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import JobRotationAssessmentService from "src/services/jobRotationAssessment/jobRotationAssessmentService";
import { ProcessPathToActivityCsv } from "src/services/assessment/types/processPathToActivityCsv";
import { SiteToProcessPathCsv } from "src/services/assessment/types/siteToProcessPathCsv";
import CsvToJsonService from "src/services/csvToJsonService";
import { SiteCsv } from "src/services/assessment/types/siteCsv";
import { getUpdatedAssessmentStatus } from "src/utils/jobRotationUtils";
import {
  assessmentFromAPIStatus,
  baseCompletedAssessment,
} from "src/jobRotationAssessment/constants/jobRotationConstants";
import {
  ComplementaryJob,
  DeleteAssessmentRequest,
} from "src/store/createJobRotationAssessmentSlice";
import { RootState } from "./store";

export interface GetJobRotationApiResponse {
  assessmentId: string;
  assessmentName: string;
  site: string;
  processPath: string;
  status: string;
  dateCreated: string;
  dateUpdated: string;
  updatedBy: string;
  strainIndexScore: number;
  numComplementaryJobs: number;
  trunkScore: number;
  upperArmScore: number;
  complementaryJobs: ComplementaryJob[];
}
export interface JobRotationAssessment extends GetJobRotationApiResponse {}
export interface CompletedAssessmentInfo extends JobRotationAssessment {}
export type JobRotationAssessmentLibraryState = {
  draftAssessments: JobRotationAssessment[];
  completedAssessments: JobRotationAssessment[];
  selectedTab: "Completed" | "Draft";
  assessmentSite: string;
  siteType: string;
  sites: SiteCsv[];
  siteToProcesspaths: SiteToProcessPathCsv[];
  unavailableProcessPaths: string[];
  error: string;
  searchTerm: string;
  tableDataToDisplay: JobRotationAssessment[];
  isDraftAssessmentCompleted: boolean;
  completedAssessmentInfo: CompletedAssessmentInfo;
  comparisonAssessmentInfo: CompletedAssessmentInfo;
  isCompletedAssessmentDeleted: boolean;
  isAssessmentOnEdit: boolean;
  isErrorOnJobRotationLibraryPage: boolean;
  deletedAssessmentInfo?: { assessmentId: string; status: string };
};

const initialState: JobRotationAssessmentLibraryState = {
  draftAssessments: [],
  completedAssessments: [],
  selectedTab: "Draft",
  assessmentSite: "ABE2",
  siteType: "Traditional Sortable Softlines",
  sites: [],
  siteToProcesspaths: [],
  unavailableProcessPaths: [],
  searchTerm: "",
  tableDataToDisplay: [],
  error: "",
  isDraftAssessmentCompleted: false,
  completedAssessmentInfo: { ...baseCompletedAssessment },
  comparisonAssessmentInfo: { ...baseCompletedAssessment },
  isCompletedAssessmentDeleted: false,
  isAssessmentOnEdit: false,
  isErrorOnJobRotationLibraryPage: false,
};

/**
 * get all job rotation assessments
 * @returns {Object}
 */
export const getAllJobRotationAssessments = createAsyncThunk(
  "jobRotationAssessmentLibrary/getJobRotationAssessments",
  async (assessmentSite: string, { dispatch, getState }) => {
    try {
      const state = await (getState() as RootState);
      const isCalculationV2 = state.globalLayout.calculationV2FeatureFlag;
      const siteDetail = state.data.currentSiteDetails;
      const response =
        await JobRotationAssessmentService.getAllJobRotationAssessments(
          assessmentSite,
          isCalculationV2
        );
      if (!response) {
        dispatch(setIsErrorOnJobRotationLibraryPage(true));
      }
      dispatch(resetUnavailableProcessPaths());
      const assessmentsfromApi: GetJobRotationApiResponse[] =
        response.data.assessments;
      const modifiedAssessments: JobRotationAssessment[] =
        assessmentsfromApi.map((assessment) => ({
          ...assessment,
          status: getUpdatedAssessmentStatus(
            assessment.status,
            assessment.dateCreated
          ),
        }));
      const sortedByUpdatedTimestamp = modifiedAssessments.sort((a, b) => {
        return (
          new Date(b.dateUpdated).getTime() - new Date(a.dateUpdated).getTime()
        );
      });
      sortedByUpdatedTimestamp.map((assessment) => {
        dispatch(setUnavailableProcessPaths(assessment.processPath));
      });
      dispatch(setUnavailableProcessPaths);
      const draftAssessments = sortedByUpdatedTimestamp.filter(
        (assessment) => assessment.status !== assessmentFromAPIStatus.DONE
      );
      const completedAssessments = sortedByUpdatedTimestamp.filter(
        (assessment) => assessment.status === assessmentFromAPIStatus.DONE
      );
      return { draftAssessments, completedAssessments };
    } catch (error: any) {
      return error.message;
    }
  }
);
export const getSitesData = createAsyncThunk(
  "jobRotationAssessmentLibrary/getSitesData",
  async () => {
    const region: string = "us-east-1";
    let sitesDataSrc, sitesToProcessPathsDataSrc;
    let processPathsToActivitiesDataSrc =
      require("src/assets/austin_process_path_to_activities.csv").default;
    switch (region) {
      case "eu-west-1": {
        sitesDataSrc = require("src/assets/austin_sites_eu.csv").default;
        sitesToProcessPathsDataSrc =
          require("src/assets/austin_site_to_process_paths_eu.csv").default;
        break;
      }
      case "ap-northeast-1": {
        sitesDataSrc = require("src/assets/austin_sites_fe.csv").default;
        sitesToProcessPathsDataSrc =
          require("src/assets/austin_site_to_process_paths_fe.csv").default;
        break;
      }
      default: {
        sitesDataSrc = require("src/assets/austin_sites_na.csv").default;
        sitesToProcessPathsDataSrc =
          require("src/assets/austin_site_to_process_paths_na.csv").default;
      }
    }

    const sites: SiteCsv[] = await CsvToJsonService.fetchCsvAndConvertToJson(
      sitesDataSrc
    );
    const siteToProcessPaths: SiteToProcessPathCsv[] =
      await CsvToJsonService.fetchCsvAndConvertToJson(
        sitesToProcessPathsDataSrc
      );
    const processPathToActivities: ProcessPathToActivityCsv[] =
      await CsvToJsonService.fetchCsvAndConvertToJson(
        processPathsToActivitiesDataSrc
      );
    return { sites, siteToProcessPaths, processPathToActivities };
  }
);
export const deleteJobRotationAssessment = createAsyncThunk(
  "createJobRotationAssessment/deleteJobRotationAssessment",
  async (deleteAssessmentRequest: DeleteAssessmentRequest, { dispatch }) => {
    const response =
      await JobRotationAssessmentService.deleteJobRotationAssessment(
        deleteAssessmentRequest
      );
    if (response.status === 200) {
      dispatch(setIsCompletedAssessmentDeleted(true));
    }
    return {
      responseStatus: response.status,
      deletedAssessment: deleteAssessmentRequest.deletedAssessment,
    };
  }
);

/** Job Rotation Assessment Library Slice */
const { reducer, actions } = createSlice({
  name: "jobRotationAssessmentLibrary",
  initialState,
  reducers: {
    setAssessmentSite: (state, { payload }) => {
      state.assessmentSite = payload;
    },
    setSearchTerm: (state, { payload }) => {
      state.searchTerm = payload;
    },
    setTableDataToDisplay: (state, { payload }) => {
      state.tableDataToDisplay = payload;
    },
    setSelectedTab: (state, { payload }) => {
      state.selectedTab = payload;
    },
    setDeletedAssessmentInfo: (state, { payload }) => {
      state.deletedAssessmentInfo = payload;
    },
    setIsDraftAssessmentCompleted: (state, { payload }) => {
      state.isDraftAssessmentCompleted = payload;
    },

    setCompletedAssessmentInfo: (state, { payload }) => {
      state.completedAssessmentInfo = payload;
    },
    setComparisonAssessmentInfo: (state, { payload }) => {
      state.comparisonAssessmentInfo = payload;
    },
    setIsCompletedAssessmentDeleted: (state, { payload }) => {
      state.isCompletedAssessmentDeleted = payload;
    },
    setIsAssessmentOnEdit: (state, { payload }) => {
      state.isAssessmentOnEdit = payload;
    },
    setIsErrorOnJobRotationLibraryPage: (state, { payload }) => {
      state.isErrorOnJobRotationLibraryPage = payload;
    },
    setUnavailableProcessPaths: (state, { payload }) => {
      state.unavailableProcessPaths.push(payload);
    },
    resetUnavailableProcessPaths: (state) => {
      state.unavailableProcessPaths = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllJobRotationAssessments.fulfilled, (state, { payload }) => {
        state.draftAssessments = payload.draftAssessments;
        state.completedAssessments = payload.completedAssessments;
      })
      .addCase(getAllJobRotationAssessments.rejected, (state, { payload }) => {
        state.error = (payload as string) || "Error fetching assessments";
      });
    builder
      .addCase(getSitesData.fulfilled, (state, { payload }) => {
        state.sites = payload.sites;
        state.siteToProcesspaths = payload.siteToProcessPaths;
      })
      .addCase(getSitesData.rejected, (state, { payload }) => {
        state.error = (payload as string) || "Error fetching assessments";
      });
    builder.addCase(
      deleteJobRotationAssessment.fulfilled,
      (state, { payload }) => {
        if (payload.deletedAssessment.status === assessmentFromAPIStatus.DONE) {
          state.completedAssessments = state.completedAssessments.filter(
            (assessment) =>
              assessment.assessmentId !== payload.deletedAssessment.assessmentId
          );
        } else {
          state.draftAssessments = state.draftAssessments.filter(
            (assessment) =>
              assessment.assessmentId !== payload.deletedAssessment.assessmentId
          );
        }
      }
    );
  },
});

export const {
  setAssessmentSite,
  setSearchTerm,
  setTableDataToDisplay,
  setSelectedTab,
  setDeletedAssessmentInfo,
  setIsDraftAssessmentCompleted,
  setCompletedAssessmentInfo,
  setComparisonAssessmentInfo,
  setIsCompletedAssessmentDeleted,
  setIsAssessmentOnEdit,
  setIsErrorOnJobRotationLibraryPage,
  setUnavailableProcessPaths,
  resetUnavailableProcessPaths,
} = actions;

export default reducer;
